import { createSelector } from 'reselect';
import { isAfter, parse } from 'date-fns';

import { campaigns as campaignsEntity } from 'store/saga/entities/bound';
import { makePath } from 'helpers/link-generation';
import { selectRouteParams, selectUserVenuesAsMap } from 'selectors/general';
import { EDITABLE_CAMPAIGN_LEVELS, EDITABLE_CAMPAIGN_TYPES } from './Campaigns.helpers';

export const selectCampaignOptions = createSelector(
  (state, venueId) => {
    const key = campaignsEntity.defaultKey(null, campaignsEntity.method.options, { venueId });
    return campaignsEntity.select(state, key);
  },
  ({ data, error, loading, loaded }) => {
    const filtered = [];
    if (loaded) {
      data.forEach(option => {
        const { available, partnerCampaignType, ...restOption } = option;
        // Time-based campaigns should not appear
        // in the standard campaign options list until the dedicated UI is built.
        if (partnerCampaignType.name === 'TIME_SLOT_ALL_USER') {
          return;
        }
        const index = filtered.findIndex(element => element.campaignType === partnerCampaignType.name);
        if (index >= 0) {
          filtered[index].availableLevels.push({
            id: partnerCampaignType.id,
            level: partnerCampaignType.level,
            name: partnerCampaignType.name,
            available,
            defaultDiscount: partnerCampaignType.defaultDiscount,
            handledByPartner: partnerCampaignType.handledByPartner,
          });
        } else {
          filtered.push({
            ...restOption,
            campaignType: partnerCampaignType.name,
            availableLevels: [
              {
                id: partnerCampaignType.id,
                level: partnerCampaignType.level,
                name: partnerCampaignType.name,
                available,
                defaultDiscount: partnerCampaignType.defaultDiscount,
                discountDecimal: option.discountPercentage / 100,
                handledByPartner: partnerCampaignType.handledByPartner,
              },
            ],
          });
        }
      });
    }

    return {
      data: !loaded ? [] : filtered,
      error,
      isLoaded: loaded,
      isLoading: loading,
    };
  }
);

export function compareCampaigns(a, b) {
  const { hasStarted: aHasStarted, hasEnded: aHasEnded, startDateTime: aStartDateTime, endDateTime: aEndDateTime } = a;
  const { hasStarted: bHasStarted, hasEnded: bHasEnded, startDateTime: bStartDateTime, endDateTime: bEndDateTime } = b;
  const aIsRunning = aHasStarted && !aHasEnded;
  const bIsRunning = bHasStarted && !bHasEnded;

  // primary: future > running > ended
  if (aHasStarted !== bHasStarted) {
    return aHasStarted ? 1 : -1;
  }
  if (aIsRunning !== bIsRunning) {
    return aIsRunning ? -1 : 1;
  }

  // secondary: future|startDateASC, running|endDateASC, ended|endDateDESC
  if (!aHasStarted) {
    return aStartDateTime.getTime() - bStartDateTime.getTime();
  }
  if (aIsRunning) {
    if (!!aEndDateTime !== !!bEndDateTime) {
      return aEndDateTime ? -1 : 1;
    }
    if (!aEndDateTime && !bEndDateTime) {
      return aStartDateTime.getTime() - bStartDateTime.getTime();
    }

    return aEndDateTime.getTime() - bEndDateTime.getTime();
  }

  return bEndDateTime.getTime() - aEndDateTime.getTime();
}

export const selectCampaigns = createSelector(
  (state, venueId) => {
    const key = campaignsEntity.defaultKey(null, campaignsEntity.method.read, { venueId });
    return campaignsEntity.select(state, key);
  },
  (_, venueId) => venueId,
  selectUserVenuesAsMap,
  ({ data, error, loading, loaded }, venueId, venuesById) => ({
    data: !loaded
      ? []
      : data
          .map(campaign => {
            const {
              startDate,
              endDate,
              partnerCampaignId,
              redemptions,
              partnerCampaignType,
              revenue,
              discountPercentage,
            } = campaign;
            const startDateTime = parse(startDate, 'yyyy-MM-dd', new Date());
            const endDateTime = !endDate ? null : parse(endDate, 'yyyy-MM-dd', new Date());
            const today = new Date();
            const hasStarted = isAfter(today, startDateTime);
            const hasEnded = !endDate ? false : isAfter(today, endDateTime);
            const { name, level } = partnerCampaignType;
            const isEditable =
              partnerCampaignId &&
              EDITABLE_CAMPAIGN_TYPES.includes(name) &&
              EDITABLE_CAMPAIGN_LEVELS.includes(level) &&
              !hasEnded;
            const { currencyCode } = venuesById[venueId];

            return {
              ...campaign,
              discountDecimal: discountPercentage / 100,
              redemptions: redemptions || 0,
              revenue: revenue || { amount: 0, currency: currencyCode },
              startDateTime,
              endDateTime,
              hasStarted,
              hasEnded,
              isEditable,
              editUrl: !isEditable ? null : makePath.campaignsEdit({ venueId, partnerCampaignId }),
            };
          })
          .sort(compareCampaigns),
    error,
    isLoaded: loaded,
    isLoading: loading,
  })
);

export const selectCurrentCampaign = createSelector(
  state => {
    const { venueId, partnerCampaignId } = selectRouteParams(state);
    const key = campaignsEntity.defaultKey(null, campaignsEntity.method.getOne, { venueId, partnerCampaignId });
    return campaignsEntity.select(state, key);
  },
  ({ loading: isLoading, loaded: isLoaded, data }) => {
    if (isLoaded) {
      const { startDate, endDate, partnerCampaignId, partnerCampaignType } = data.data;
      const startDateTime = parse(startDate, 'yyyy-MM-dd', new Date());
      const endDateTime = !endDate ? null : parse(endDate, 'yyyy-MM-dd', new Date());
      const today = new Date();
      const hasStarted = isAfter(today, startDateTime);
      const hasEnded = !endDate ? false : isAfter(today, endDateTime);
      const { name, level } = partnerCampaignType;
      const isEditable =
        partnerCampaignId &&
        EDITABLE_CAMPAIGN_TYPES.includes(name) &&
        EDITABLE_CAMPAIGN_LEVELS.includes(level) &&
        !hasEnded;

      return {
        campaign: {
          ...data.data,
          hasStarted,
          hasEnded,
          isEditable,
        },
        isLoading,
        isLoaded,
      };
    }

    return {
      campaign: {},
      isLoading,
      isLoaded,
    };
  }
);
